/**
 * Boundary between the UI's form values and the backend's GraphQL enum types
 * for query-level settings.
 *
 * Format-related enum maps live in `shared/DataFormatSection/enumMaps.ts`
 * and rule mappers live in `shared/RulesSection/mappers.ts` — both are
 * shared with `FieldSettingsPanel/fieldSettingsMapper.ts` so neither
 * panel reaches into the other's mapper. This file owns the
 * query-only pieces (footnote symbol, `QuerySettingsInput` assembly,
 * server-only round-trip).
 *
 * Backend contract:
 *   - `finpubQueryUpdate` is copy-on-write: omitting `fields` / `footnotes`
 *     carries them forward, so `{ queryId, name, settings }` is safe.
 *   - `customMessageEnabled` is round-tripped through `serverOnly` until
 *     the UI surfaces it.
 */

import { DEFAULT_DATE_FORMAT_PATTERN } from "../../../../shared/DataFormatSection/constants";
import {
  CURRENCY_POSITION_FROM_BE,
  CURRENCY_POSITION_TO_BE,
  NEGATIVE_NUMBERS_FROM_BE,
  NEGATIVE_NUMBERS_TO_BE,
  NULL_DISPLAY_FROM_BE,
  NULL_DISPLAY_TO_BE,
  NUMBER_SCALE_FROM_BE,
  NUMBER_SCALE_TO_BE,
  ZERO_DISPLAY_FROM_BE,
  ZERO_DISPLAY_TO_BE,
} from "../../../../shared/DataFormatSection/enumMaps";
import { dateFormatPatternOrFallback } from "../../../../shared/DataFormatSection/patternBuilder";
import {
  DEFAULT_CONDITION_OPERATOR,
  DEFAULT_GROUPS_OPERATOR,
} from "../../../../shared/RulesSection/constants";
import { toRules, toRulesInput } from "../../../../shared/RulesSection/mappers";
import { DEFAULT_QUERY_SETTINGS_VALUES } from "./constants";
import type { FootnoteSymbolOption, QuerySettingsValues } from "./types";
import type {
  FinpubQueryQuery,
  FootnoteSymbolStyle,
  QuerySettingsInput,
} from "~/graphql/supergraphTypes";

// Re-exports kept for back-compat; canonical sources live in `shared/`.
export { DEFAULT_CONDITION_OPERATOR, DEFAULT_GROUPS_OPERATOR };

// -- Footnote symbol map (query-only) -----------------------------------------

// Exported so the footnote settings panel maps the per-footnote
// symbol override against the same source of truth as the query default.
export const FOOTNOTE_SYMBOL_TO_BE: Record<FootnoteSymbolOption, FootnoteSymbolStyle> = {
  numeric: "NUMERIC",
  lowercase_letters: "LOWERCASE_LETTERS",
  uppercase_letters: "UPPERCASE_LETTERS",
  lowercase_roman: "LOWERCASE_ROMAN",
  uppercase_roman: "UPPERCASE_ROMAN",
  symbols: "SYMBOLS",
};

export const FOOTNOTE_SYMBOL_FROM_BE: Record<FootnoteSymbolStyle, FootnoteSymbolOption> = {
  NUMERIC: "numeric",
  LOWERCASE_LETTERS: "lowercase_letters",
  UPPERCASE_LETTERS: "uppercase_letters",
  LOWERCASE_ROMAN: "lowercase_roman",
  UPPERCASE_ROMAN: "uppercase_roman",
  SYMBOLS: "symbols",
};

// -- Top-level round-trip -----------------------------------------------------

/** Fields the BE stores but the UI doesn't surface yet — round-tripped
 * verbatim so saving never clobbers server state. */
export type ServerOnlySettings = {
  customMessageEnabled: boolean;
};

export const DEFAULT_SERVER_ONLY_SETTINGS: ServerOnlySettings = {
  customMessageEnabled: false,
};

export type MappedQuerySettings = {
  /** Formik form values. Hand straight to `<Form initialValues>`. */
  values: QuerySettingsValues;
  /** Re-attach via `toQuerySettingsInput(values, serverOnly)` on save. */
  serverOnly: ServerOnlySettings;
};

/** Backend `QueryDetail` → form values + server-only slice. Falls back to
 * defaults when `query` or `query.settings` is null (so the user can still
 * edit + save from scratch). */
export const toQuerySettingsValues = (
  query: FinpubQueryQuery["finpubQuery"],
): MappedQuerySettings => {
  const supportedTransactionTypes =
    query?.transactionTypeNodes?.map((node) => node.typeHierarchyNodeId) ?? [];

  if (!query || !query.settings) {
    return {
      values: {
        ...DEFAULT_QUERY_SETTINGS_VALUES,
        name: query?.name ?? DEFAULT_QUERY_SETTINGS_VALUES.name,
        supportedTransactionTypes,
      },
      serverOnly: DEFAULT_SERVER_ONLY_SETTINGS,
    };
  }
  const { settings } = query;
  return {
    values: {
      name: query.name,
      decimalPlaces: settings.decimalPlaces,
      formatWithinQuery: settings.formatWithinQuery,
      negativeNumbers: NEGATIVE_NUMBERS_FROM_BE[settings.negativeNumberStyle],
      numberScale: NUMBER_SCALE_FROM_BE[settings.numberScale],
      thousandsSeparator: settings.thousandsSeparator,
      showCurrencySymbol: settings.showCurrencySymbol,
      currencySymbolPosition: CURRENCY_POSITION_FROM_BE[settings.currencySymbolPosition],
      zeroDisplay: ZERO_DISPLAY_FROM_BE[settings.zeroDisplayStyle],
      nullDisplay: NULL_DISPLAY_FROM_BE[settings.nullDisplayStyle],
      dateFormatPattern: dateFormatPatternOrFallback(
        settings.dateFormatPattern,
        DEFAULT_DATE_FORMAT_PATTERN,
      ),
      footnoteSymbol: FOOTNOTE_SYMBOL_FROM_BE[settings.footnoteSymbolStyle],
      supportedTemplateType: settings.supportedTemplateType,
      supportedTransactionTypes,
      filterRules: toRules(settings.filterRules),
      suppressionRules: toRules(settings.suppressionRules),
    },
    serverOnly: {
      customMessageEnabled: settings.customMessageEnabled,
    },
  };
};

/** Form values + server-only slice → `QuerySettingsInput`. `name` is
 * intentionally omitted — it rides on the mutation arg directly. */
export const toQuerySettingsInput = (
  values: QuerySettingsValues,
  serverOnly: ServerOnlySettings,
): QuerySettingsInput => ({
  decimalPlaces: values.decimalPlaces,
  formatWithinQuery: values.formatWithinQuery,
  negativeNumberStyle: NEGATIVE_NUMBERS_TO_BE[values.negativeNumbers],
  numberScale: NUMBER_SCALE_TO_BE[values.numberScale],
  thousandsSeparator: values.thousandsSeparator,
  showCurrencySymbol: values.showCurrencySymbol,
  currencySymbolPosition: CURRENCY_POSITION_TO_BE[values.currencySymbolPosition],
  zeroDisplayStyle: ZERO_DISPLAY_TO_BE[values.zeroDisplay],
  nullDisplayStyle: NULL_DISPLAY_TO_BE[values.nullDisplay],
  dateFormatPattern: values.dateFormatPattern,
  filterRules: toRulesInput(values.filterRules),
  suppressionRules: toRulesInput(values.suppressionRules),
  footnoteSymbolStyle: FOOTNOTE_SYMBOL_TO_BE[values.footnoteSymbol],
  supportedTemplateType: values.supportedTemplateType,
  customMessageEnabled: serverOnly.customMessageEnabled,
});
